/**
 * Modal de Cobertura Técnica (GUPM-199) — fiel ao `buildTechCoverageWidget` do V8.
 * Mostra o estado do dia (ativa / sobreaviso) + os RTs da cadeia técnica. Os
 * contatos vêm do BANCO (datasource `contatos`, gate por RLS) — nunca hardcoded
 * (security.md 🟠). Fetch é lazy: o modal só monta (e busca) quando abre.
 *
 * Estados distintos: carregando ≠ erro ≠ vazio — numa cadeia de emergência um erro
 * de fetch não pode se disfarçar de "sem contatos" (mesmo princípio do modal de
 * emergência do header, achado de review #93).
 */
import { coberturaTecnica } from '@/domain/cobertura';
import { useContatosEmergencia } from '@/hooks/useContatosEmergencia';
import { usePopup } from '@/hooks/usePopup';
import type { ContatoEmergencia } from '@/services/datasources/contatos';
import { PhoneIcon } from '@/components/Header/PhoneIcon';
import { EtiquetasFuncao } from '@/ui/EtiquetasFuncao';

function LinhaRt({ contato }: { contato: ContatoEmergencia }) {
  const temTelefone = contato.phone !== '';
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/5">
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-white truncate">{contato.name}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <EtiquetasFuncao contato={contato} />
          {contato.disponibilidade ? <span className="text-[10.5px] text-jf-dim">{contato.disponibilidade}</span> : null}
        </div>
      </div>
      {temTelefone ? (
        <a
          href={`tel:${contato.phone}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold bg-jf-yellow text-jf-bg whitespace-nowrap"
        >
          <PhoneIcon size={13} />
          {contato.display}
        </a>
      ) : (
        // pendente (PII real só no cloud — GUPM-158): sem link, mesmo tamanho.
        <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs italic font-semibold text-jf-dim border border-jf-border whitespace-nowrap">
          {contato.display}
        </span>
      )}
    </div>
  );
}

export function CoberturaTecnicaModal({ onClose }: { onClose: () => void }) {
  const { data: contatos = [], isLoading, isError } = useContatosEmergencia();
  const { ativa, rts } = coberturaTecnica(contatos);
  const vazio = !isLoading && !isError && rts.length === 0;
  const ref = usePopup<HTMLDivElement>({ aoFechar: onClose });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 p-4 pt-20"
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Cobertura técnica"
        tabIndex={-1}
        className="focus:outline-none w-full max-w-md rounded-xl bg-jf-surface border border-jf-border shadow-xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 pt-5 pb-3">
          <h2 className="text-base font-black text-white flex items-center gap-2">🛡️ Cobertura Técnica</h2>
          <div
            className={`mt-2 rounded-lg px-3 py-2 border text-xs font-bold leading-relaxed ${
              ativa
                ? 'border-level-normal/40 bg-level-normal/10 text-level-normal'
                : 'border-level-alerta/40 bg-level-alerta/10 text-level-alerta'
            }`}
          >
            {ativa
              ? '🟢 Cobertura técnica ativa — RT Foscentro no plantão (08h–18h dias úteis).'
              : '🟠 Fora do horário técnico — RT JF Brasil de sobreaviso. Em anomalia, siga o protocolo TARP.'}
          </div>
        </header>

        <div className="overflow-y-auto px-5 pb-2">
          {isLoading ? (
            <p className="text-jf-muted text-sm py-2">Carregando contatos…</p>
          ) : isError ? (
            <p role="alert" className="text-red-300 text-sm py-2">
              Não foi possível carregar os contatos. Verifique a conexão e tente novamente.
            </p>
          ) : vazio ? (
            <p className="text-jf-muted text-sm py-2">
              Nenhum RT cadastrado na cadeia técnica. O cadastro é feito pelo administrador.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rts.map((rt) => (
                <LinhaRt key={rt.id} contato={rt} />
              ))}
            </div>
          )}
          <p className="text-[10.5px] text-jf-dim border-t border-jf-border pt-2 mt-2 leading-relaxed">
            Em horário administrativo a notificação vai simultaneamente ao RT Foscentro e ao RT JF Brasil.
            Fora dele, segue a escala de sobreaviso.
          </p>
        </div>

        <footer className="flex gap-2.5 px-5 pt-3 pb-5 border-t border-jf-border">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-[7px] px-[14px] rounded-lg text-xs font-bold text-white bg-jf-surface2 hover:bg-jf-border transition-colors"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
